import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import { useForm } from '../../hooks';
import * as patientSelector from '../../selectors/patient';
import { RootDispatch, RootState } from '../../store';
import { orgPhone } from '../../selectors/organization';
import { ShowMessengerPayload, AuthMethod } from '../../types';
import OrgHeader from '../OrgHeader';

import styles from './AccessCodeForm.module.css';

enum KeyCode {
  BACKSPACE = 8,
  ENTER = 13,
  SPACE = 32,
  ZERO = 48,
  NINE = 57,
}

const dateStringToDate = (dateString: string): Date => {
  const month = parseInt(dateString.slice(0, 2));
  const day = parseInt(dateString.slice(2, 4));
  const year = parseInt(dateString.slice(4, 9));
  if (
    !(month && month >= 1 && month <= 12 && day && day >= 1 && day <= 31 && year && year > 1900)
  ) {
    throw new Error('Not a valid date.');
  }
  return new Date(Date.UTC(year, month - 1, day));
};

const validAccessCode = (accessCode: string, needsAuth: string[]): boolean => {
  if (!needsAuth.includes('access_token')) return true;

  return !!accessCode && accessCode.length >= 6;
};

const validBirthday = (birthday: string, needsAuth: string[]): boolean => {
  if (!needsAuth.includes('dob')) return true;

  try {
    const date = dateStringToDate(birthday);
    return !!date;
  } catch (e) {
    return false;
  }
};

export function AccessCodeFormComponent({
  errorCount,
  fetchAccessToken,
  needsAuth,
  phoneNumber,
  showMessenger,
}: {
  errorCount: number;
  fetchAccessToken: () => Promise<Promise<void>>;
  needsAuth: string[];
  phoneNumber: string;
  showMessenger: (payload: ShowMessengerPayload) => void;
}) {
  const afterSubmit = (latestInputs: {
    accessCode: string;
    birthdayMonth: string;
    birthdayDay: string;
    birthdayYear: string;
  }): void => {
    const birthday =
      latestInputs.birthdayMonth + latestInputs.birthdayDay + latestInputs.birthdayYear;

    const payload: ShowMessengerPayload = {};

    payload.accessCode = latestInputs.accessCode;

    if (needsAuth.includes(AuthMethod.Dob)) {
      const dob = dateStringToDate(birthday).toISOString().split('T')[0];

      payload.dob = dob;
    }

    showMessenger(payload);
  };

  const handleAuthInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.name) {
      case 'birthdayMonth':
        if (event.target.value && event.target.value.length === 2) {
          let elem = document.getElementById('birthdayDay') as HTMLInputElement;
          if (elem && elem.value.length >= 0) {
            elem.focus();
            if (elem.value.length > 0) {
              elem.select();
            }
          }
        }
        break;
      case 'birthdayDay':
        if (event.target.value && event.target.value.length === 2) {
          let elem = document.getElementById('birthdayYear') as HTMLInputElement;
          if (elem && elem.value.length >= 0) {
            elem.focus();
            if (elem.value.length > 0) {
              elem.select();
            }
          }
        }
        break;
      case 'accessCode':
        if (event.target.value && event.target.value.length === 6) {
          let elem = document.getElementById('birthdayMonth') as HTMLInputElement;
          if (elem && elem.value.length >= 0) {
            elem.focus();
            if (elem.value.length > 0) {
              elem.select();
            }
          }
        }
        break;
    }
    return handleInputChange(event);
  };

  const { handleInputChange, handleSubmit, inputs } = useForm(
    {
      accessCode: '',
      birthdayMonth: '',
      birthdayDay: '',
      birthdayYear: '',
    },
    afterSubmit
  );

  let onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    //KeyCode 8 is backspace
    if (e.keyCode === KeyCode.BACKSPACE) {
      const currentTargetId = (e.target as HTMLInputElement).id;
      switch (currentTargetId) {
        case 'birthdayDay':
          if (inputs.birthdayDay.length === 0) {
            let elem = document.getElementById('birthdayMonth') as HTMLInputElement;
            if (elem) {
              elem.focus();
              elem.select();
            }
          }
          break;
        case 'birthdayYear':
          if (inputs.birthdayYear.length === 0) {
            let elem = document.getElementById('birthdayDay') as HTMLInputElement;
            if (elem) {
              elem.focus();
              elem.select();
            }
          }
          break;
      }
    } else if (e.keyCode === KeyCode.ENTER) {
      handleSubmit(e);
    } else if (
      e.keyCode !== KeyCode.BACKSPACE &&
      e.keyCode !== KeyCode.SPACE &&
      !(e.keyCode >= KeyCode.ZERO && e.keyCode <= KeyCode.NINE)
    ) {
      //KeyCode 8 is backspace, 32 is space
      e.preventDefault();
    }
  };

  const birthday = inputs.birthdayMonth + inputs.birthdayDay + inputs.birthdayYear;
  const enableSubmit =
    validAccessCode(inputs.accessCode, needsAuth) && validBirthday(birthday, needsAuth);
  const attemptsLeft = 5 - errorCount;
  let authError = errorCount > 0 && (
    <div className={styles.error}>
      <FormattedMessage
        id={'accessCodeForm.loginErrorDetails'}
        values={{ number: attemptsLeft, placeholder: attemptsLeft > 1 ? 'attempts' : 'attempt' }}
      />
    </div>
  );

  return (
    <div className={styles.container}>
      <OrgHeader className={styles.orgHeader} />
      <form className={styles.form}>
        {needsAuth.includes('access_token') && (
          <label>
            <FormattedMessage id={'accessCodeForm.accessCodeInputLabel'} />
            <input
              className={styles.accessCodeInput}
              autoFocus
              type="tel"
              pattern="[0-9]*"
              id="accessCode"
              name="accessCode"
              maxLength={6}
              tabIndex={0}
              placeholder="000000"
              onChange={handleAuthInputChange}
              onKeyDown={onKeyDown}
            />
          </label>
        )}
        {needsAuth.includes('dob') && (
          <div>
            <div className={styles.splitInputLabel}>
              <FormattedMessage id={'accessCodeForm.birthdayInputLabel'} />
            </div>
            <div className={styles.splitInputContainer}>
              <label>
                <input
                  type="tel"
                  pattern="[0-9]*"
                  id="birthdayMonth"
                  name="birthdayMonth"
                  maxLength={2}
                  tabIndex={0}
                  placeholder="mm"
                  onChange={handleAuthInputChange}
                  onKeyDown={onKeyDown}
                />
              </label>
              <label>
                <input
                  className={styles.birthdayInput}
                  type="tel"
                  pattern="[0-9]*"
                  id="birthdayDay"
                  name="birthdayDay"
                  maxLength={2}
                  tabIndex={0}
                  placeholder="dd"
                  onChange={handleAuthInputChange}
                  onKeyDown={onKeyDown}
                />
              </label>
              <label>
                <input
                  className={styles.birthdayInput}
                  type="tel"
                  pattern="[0-9]*"
                  id="birthdayYear"
                  name="birthdayYear"
                  maxLength={4}
                  tabIndex={0}
                  placeholder="yyyy"
                  onChange={handleAuthInputChange}
                  onKeyDown={onKeyDown}
                />
              </label>
            </div>
          </div>
        )}
        <button type="button" disabled={!enableSubmit} onClick={handleSubmit}>
          <FormattedMessage id={'accessCodeForm.accessConversationButton'} />
        </button>
        {authError}
        <div className={styles.agreement}>
          <FormattedMessage id={'accessCodeForm.licenseAgreement'} />
          <a
            href="https://v4-web-static.s3.amazonaws.com/hosted/eula/v2/tigertouch-eula-v2.0/eula.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id={'accessCodeForm.openLicenseAgreement'} />
          </a>
        </div>
      </form>
      <div className={styles.footer}>
        {needsAuth.includes('access_token') && (
          <div className={styles.footerCopy}>
            <FormattedMessage id={'accessCodeForm.supportResendSubFooter'} />
            &nbsp;
            <button
              className={styles.linkifiedTextButton}
              aria-label="show-input-access-code"
              onClick={async () => {
                await fetchAccessToken();
              }}
            >
              <FormattedMessage id={'accessCodeForm.resendButton'} />
            </button>
          </div>
        )}
        {phoneNumber && (
          <div className={styles.footerCopy}>
            <FormattedMessage id={'accessCodeForm.callSupportSubFooter'} />
            <br />
            <a
              className={styles.linkifiedTextButton}
              aria-label="call-support"
              href={`tel:${phoneNumber}`}
            >
              {phoneNumber}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const mapState = (state: RootState) => ({
  errorCount: patientSelector.errorCount(state),
  needsAuth: patientSelector.needsAuth(state),
  phoneNumber: orgPhone(state),
});

const mapDispatch = ({
  currentPage: { showInputAccessCode, showMessenger, fetchAccessToken },
}: RootDispatch) => ({
  fetchAccessToken,
  showInputAccessCode,
  showMessenger,
});

// https://tigertext.atlassian.net/browse/TMG-837
// @ts-expect-error
export const AccessCodeForm = connect(mapState, mapDispatch)(AccessCodeFormComponent);
